import { useState, useEffect, useCallback } from 'react';
import { apiFetch } from '../utils/api';

export interface PushEvent {
    id: string;
    label: string;
    desc: string;
}

export interface PushPreferences {
    [eventId: string]: boolean;
}

export type PermissionState = 'default' | 'granted' | 'denied' | 'unsupported';

function urlBase64ToUint8Array(base64String: string): Uint8Array {
    const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
    const base64  = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
    const raw     = window.atob(base64);
    const arr = new Uint8Array(raw.length);
    for (let i = 0; i < raw.length; i++) arr[i] = raw.charCodeAt(i);
    return arr;
}

export function usePushNotifications() {
    const [permission,   setPermission]   = useState<PermissionState>('default');
    const [subscribed,   setSubscribed]   = useState(false);
    const [loading,      setLoading]      = useState(false);
    const [preferences,  setPreferences]  = useState<PushPreferences>({});
    const [events,       setEvents]       = useState<PushEvent[]>([]);
    const [supported,    setSupported]    = useState(false);

    useEffect(() => {
        const ok = 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
        setSupported(ok);
        if (ok) setPermission(Notification.permission as PermissionState);
    }, []);

    // Load preferences from server
    const loadPreferences = useCallback(async () => {
        try {
            const res  = await apiFetch('/api/push/preferences');
            const data = await res.json();
            if (data.success) {
                setPreferences(data.preferences || {});
                setEvents(data.events || []);
            }
        } catch {}
    }, []);

    useEffect(() => { loadPreferences(); }, [loadPreferences]);

    // Check if already subscribed
    useEffect(() => {
        if (!supported) return;
        (async () => {
            const reg = await navigator.serviceWorker.getRegistration('/sw.js');
            if (!reg) return;
            const sub = await reg.pushManager.getSubscription();
            setSubscribed(!!sub);
        })();
    }, [supported]);

    const subscribe = useCallback(async () => {
        if (!supported) return;
        setLoading(true);
        try {
            // Request permission
            const perm = await Notification.requestPermission();
            setPermission(perm as PermissionState);
            if (perm !== 'granted') return;

            // Register service worker
            const reg = await navigator.serviceWorker.register('/sw.js');
            await navigator.serviceWorker.ready;

            // Get VAPID public key
            const keyRes  = await apiFetch('/api/push/vapid-key');
            const keyData = await keyRes.json();
            const appServerKey = urlBase64ToUint8Array(keyData.publicKey);

            // Subscribe
            const sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: appServerKey });
            const subJson = sub.toJSON();

            // Save to server
            const saveRes = await apiFetch('/api/push/subscribe', {
                method: 'POST',
                body: JSON.stringify({ subscription: subJson }),
            });
            const saved = await saveRes.json();
            if (saved.success) setSubscribed(true);
        } catch (err) {
            console.error('Push subscribe error:', err);
        } finally {
            setLoading(false);
        }
    }, [supported]);

    const unsubscribe = useCallback(async () => {
        setLoading(true);
        try {
            const reg = await navigator.serviceWorker.getRegistration('/sw.js');
            if (reg) {
                const sub = await reg.pushManager.getSubscription();
                if (sub) await sub.unsubscribe();
            }
            await apiFetch('/api/push/subscribe', { method: 'DELETE' });
            setSubscribed(false);
        } catch (err) {
            console.error('Push unsubscribe error:', err);
        } finally {
            setLoading(false);
        }
    }, []);

    const updatePreference = useCallback(async (eventId: string, enabled: boolean) => {
        setPreferences(prev => ({ ...prev, [eventId]: enabled }));
        try {
            await apiFetch('/api/push/preferences', {
                method: 'PATCH',
                body: JSON.stringify({ [eventId]: enabled }),
            });
        } catch {
            // Rollback on error
            setPreferences(prev => ({ ...prev, [eventId]: !enabled }));
        }
    }, []);

    return { supported, permission, subscribed, loading, preferences, events, subscribe, unsubscribe, updatePreference };
}
